import React, { useState, useEffect, useContext } from 'react'
import classnames from 'classnames'
import {
  createStyles,
  makeStyles,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  useMediaQuery,
  Tooltip,
  NewAltIcon
} from '@paintscout/ui'
import type { MuiTheme as Theme, FeatureChipProps } from '@paintscout/ui'
import {
  useUser,
  useClientOptions,
  useDialogs,
  useUserPermissions,
  DevPermissionsContext
} from '@paintscout/ui-business'
import { useSubscriptionSummaryQuery } from '@paintscout/api'

import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import FaceIcon from '@material-ui/icons/Face'
import { useFlags } from 'shared/components/common/FlagsProvider'
import PaintScout_Icon_White from 'src/public/images/PaintScout_Icon_White.svg'
import PaintScout_Wordmark_White from 'src/public/images/PaintScout_Wordmark_White.svg'
import { getFeatures, getObjectLabels } from '@paintscout/util/builder'
import { getCRMAddonProductId } from '@paintscout/util'
import { NavLink, useLocation } from 'react-router-dom'
import { useLocalStorage } from 'shared/hooks'
import { track } from 'shared/util/analytics'
import moment from 'moment'
import OperationsPromptDialog from 'src/components/dialogs/OperationsPromptDialog'
import { useInternalStaff } from 'src/hooks/useInternalStaff'
import type { LinkDefinition } from './Links'

/** Which breadcrumb the current page asks the main header for — see NavContextValue.pageBreadcrumb. */
export type PageBreadcrumb = false | 'document' | 'invoice' | 'template'

interface NavContextValue {
  sideNavWidth: number
  setOpen?: (open: boolean) => void
  pageSubtitle?: string
  setPageSubtitle?: (subtitle: string) => void
  pageChip?: FeatureChipProps
  setPageChip?: (chip: FeatureChipProps) => void
  hideBackLink?: boolean
  setHideBackLink?: (hide: boolean) => void
  // Page-supplied controls rendered in the main header's right group (before the profile menu) — e.g.
  // the estimateNext AI Assistant + sidebar-toggle buttons.
  pageActions?: React.ReactNode
  setPageActions?: (actions: React.ReactNode) => void
  /** True while the current page wants the app header pinned to the top of the viewport. */
  stickyHeader?: boolean
  setStickyHeader?: (sticky: boolean) => void
  // Set while the current page wants the estimate breadcrumb in place of the normal page title:
  // 'document' for an estimate (← | Estimates › <contact>), 'invoice' for an invoice (← | Invoices ›
  // <contact>), 'template' for a template (← | Estimate Template — no list link, and no contact to
  // name it). Only the estimateNext editor sets it: the page owns the decision, because the header
  // cannot tell an estimate from a template (both live under /quotes/edit, ENG-1511), nor an invoice
  // opened from its estimate, which keeps the /quotes path (ENG-2062).
  pageBreadcrumb?: PageBreadcrumb
  setPageBreadcrumb?: (breadcrumb: PageBreadcrumb) => void
}

export const NavContext = React.createContext<NavContextValue>({
  sideNavWidth: 0,
  setOpen: () => {},
  pageSubtitle: '',
  setPageSubtitle: () => {},
  pageChip: undefined,
  setPageChip: () => {},
  hideBackLink: false,
  setHideBackLink: () => {},
  pageActions: undefined,
  setPageActions: () => {},
  stickyHeader: false,
  setStickyHeader: () => {},
  pageBreadcrumb: false,
  setPageBreadcrumb: () => {}
})

export function useNavContext(): NavContextValue {
  return React.useContext(NavContext)
}

const DEALS_BOARD_PATH = '/deals/board/'
const EVENTS_CALENDAR_PATH = '/events/?tab=calendar/'
const TRANSITION_EASE = 'all .4s ease'

const drawerWidth = 190
const collapsedDrawerWidth = 62.4
const tooltipDelay = 650

const useStyles = makeStyles<Theme, { sideNavWidth: number; expanded: boolean }>((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex'
    },
    drawer: {
      width: drawerWidth,
      flexShrink: 0,
      whiteSpace: 'nowrap',
      '@media print': {
        display: 'none'
      }
    },
    drawerPaper: {
      overflow: 'hidden',
      background: theme.paintscout.palette.brand?.Neutral?.P35,
      color: 'white',
      border: 'none',
      '& svg': {
        color: 'white'
      },
      '& i': {
        color: 'white',
        fontSize: theme.typography.pxToRem(18)
      }
    },
    drawerOpen: {
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen
      })
    },
    drawerClose: {
      width: collapsedDrawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen
      }),
      overflowX: 'hidden'
    },
    logoIcon: {
      width: 24,
      marginLeft: 19,
      marginTop: 4
    },
    logoWordmark: ({ expanded }) => ({
      width: 110,
      marginLeft: 7,
      marginBottom: 6,
      opacity: expanded ? 1 : 0,
      transition: 'opacity 0.1s linear'
    }),
    link: {
      textDecoration: 'none'
    },
    linkContainer: {
      margin: theme.spacing(1),
      borderRadius: theme.borderRadius.md,
      paddingTop: 0,
      paddingBottom: 0,
      width: 'auto',
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand?.Neutral?.P50
      }
    },
    activeLinkContainer: {
      backgroundColor: theme.paintscout.palette.brand?.Neutral?.P50
    },
    linkIcon: {
      minWidth: 'unset',
      width: 46.4,
      height: 46.4,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      '& active': {
        border: 'none'
      }
    },
    linkText: {
      ...theme.typography.button,
      fontWeight: 500,
      marginLeft: 8,
      color: 'white',
      '& active': {
        border: 'none'
      }
    },
    activeLinkText: {
      fontWeight: 600
    },
    drawerToggleButton: {
      height: 41,
      width: 41,
      minWidth: 41,
      minHeight: 41,
      color: theme.paintscout.palette.brand.Neutral.P75,
      borderColor: theme.paintscout.palette.brand?.Neutral?.P50,
      backgroundColor: 'transparent',
      borderWidth: 2,
      marginLeft: 10,
      marginBottom: 10,
      '&:hover': {
        backgroundColor: theme.paintscout.palette.brand?.Neutral?.P50
      }
    },
    collapseTooltip: {
      marginBottom: 10
    },
    collapseTooltipOpen: {
      display: 'none'
    },
    content: ({ sideNavWidth }) => ({
      flexGrow: 1,
      width: `calc(100% - ${sideNavWidth}px)`
    }),
    nestedLinks: {
      borderLeft: `2px solid ${theme.paintscout.palette.brand.Neutral.White}`,
      marginLeft: theme.spacing(3.75),
      paddingLeft: theme.spacing(1.75)
    },
    floatingMenu: {
      backgroundColor: theme.paintscout.palette.brand?.Neutral?.P35 ?? theme.palette.grey[600],
      borderRadius: '4px',
      zIndex: 13_000,
      '& > li': {
        marginRight: theme.spacing()
      }
    },
    nestedLinkText: {
      ...theme.typography.button,
      fontWeight: 500,
      marginLeft: 8,
      marginRight: 8,
      color: 'white',
      '& active': {
        border: 'none'
      }
    },
    devToolsActive: {
      outline: `2px solid ${theme.paintscout.palette.brand?.Neutral?.P60}`,
      outlineOffset: -2
    },
    // Every nav option on a churned account (ENG-1532). The items stay visible so the user can
    // see what they get back when they resubscribe, but nothing here navigates — SubscriptionGuard
    // would bounce the click to /dashboard anyway, and a link that silently throws you somewhere
    // else is worse than one that plainly cannot be used.
    disabledLink: {
      opacity: 0.5,
      cursor: 'default',
      pointerEvents: 'none'
    }
  })
)

interface SideNavProps {
  children?: React.ReactNode
}

export default function SideNav(props: SideNavProps) {
  const { children } = props
  const location = useLocation()
  const mdDown = useMediaQuery('md')
  const { options } = useClientOptions()
  const { crm: crmFlag, crmVisible, painterAccount, painterViewDemo } = useFlags()
  const objectLabels = getObjectLabels({ options })
  const [expanded, setExpanded] = useLocalStorage<boolean>('sideNavExpanded', true) // is the permanent drawer expanded for desktop
  const { isFieldUser, isCorporate, isMasquerading, trialExpired, user, isSubscribed, hasPermissions, isSuperadmin } =
    useUser()
  const isInternalStaff = useInternalStaff()
  const { permissionToUpdateBilling, permissionToViewReports, permissionToViewDashboard } = useUserPermissions()
  const { openDialog } = useDialogs()

  const [pageSubtitle, setPageSubtitle] = useState('')
  const [pageChip, setPageChip] = useState<FeatureChipProps | undefined>()
  const [pageActions, setPageActions] = useState<React.ReactNode>()
  const [hideBackLink, setHideBackLink] = useState(false)
  // Pages opt into a pinned app header (the estimateNext editor does — its Save/Send and the AI
  // Assistant live up there, so they have to stay reachable while a long estimate scrolls).
  const [stickyHeader, setStickyHeader] = useState(false)
  // See NavContextValue.pageBreadcrumb — set by the estimateNext editor only.
  const [pageBreadcrumb, setPageBreadcrumb] = useState<PageBreadcrumb>(false)
  const [collapseTooltipHidden, setCollapseTooltipHidden] = useState(false)
  const [open, setOpen] = useState(false) // is the temporary drawer open for mobile
  const [openNested, setOpenNested] = useState<string[]>([])
  const [openNestedCollapsed, setOpenNestedCollapsed] = useState<string | null>(null) // which nested menu is open for mobile (only one can be open at a time)
  const [floatingMenuTop, setFloatingMenuTop] = useState(0)

  useEffect(() => {
    if (mdDown) {
      setOpenNestedCollapsed(null)
    }
  }, [mdDown])

  const isExpanded = expanded || (open && mdDown)
  const sideNavWidth = isExpanded ? drawerWidth : collapsedDrawerWidth
  const classes = useStyles({ ...props, sideNavWidth, expanded: isExpanded })

  const { state: devState, setState: setDevState } = useContext(DevPermissionsContext)
  const _devStage = process.env.DEPLOYMENT_STAGE ?? 'local'
  const showDevToolsNav =
    devState.dismissed &&
    (_devStage === 'local' ||
      _devStage === 'dev' ||
      (_devStage === 'test' && window.localStorage.getItem('ps:devtools') === 'true'))

  const [crmTrialExpiration, setCrmTrialExpiration] = useState<number>(options?.options?.crm?.trialExpiration)
  useEffect(() => {
    setCrmTrialExpiration(options?.options?.crm?.trialExpiration)
  }, [options?.options?.crm?.trialExpiration])

  const crmTrialExpirationIsInFuture = !!crmTrialExpiration && moment(crmTrialExpiration).isAfter(moment())
  const crmTrialExpirationIsInPast = !!crmTrialExpiration && moment(crmTrialExpiration).isBefore(moment())
  const isOnCRMTrial = crmTrialExpirationIsInFuture && crmFlag

  const { data: subscriptionData } = useSubscriptionSummaryQuery({
    fetchPolicy: 'network-only',
    variables: {
      clientBillingId: user?.app_metadata?.company?.billingId
    }
  })
  const subscription = subscriptionData?.subscriptionSummary ?? user?.app_metadata?.company?.subscription
  const hasCRMAddon = subscription?.addons?.some((addon: any) => addon.id === getCRMAddonProductId(subscription))

  const doesNotHaveCRM =
    crmVisible &&
    !crmFlag &&
    !isOnCRMTrial &&
    !crmTrialExpirationIsInPast &&
    !hasCRMAddon &&
    // `isSubscribed` alone is the subscription gate here. `!isChurned` used to sit beside it and
    // could never be false: `isChurned` is defined as `!isSubscribed && ...`, so requiring
    // `isSubscribed` already excludes it. It also inherited isChurned's blind spot — a
    // dunning-cancelled account is unsubscribed 25+ days before its term ends (ENG-1532).
    isSubscribed &&
    !trialExpired

  // Churned accounts lose the whole nav (ENG-1532) — the block screen on /dashboard is the only
  // place left to go, plus /settings to resubscribe. Internal staff keep it: they are the people
  // helping the account come back, and they still see the block screen on /dashboard itself.
  const navBlocked = !isSubscribed && !isInternalStaff

  const isOnCRMPage =
    location.pathname.startsWith('/deals/') ||
    location.pathname.startsWith('/events/') ||
    location.pathname.startsWith('/tasks/')

  const hidePeople =
    options.options?.quotes?.forceIntegrationContacts || !hasPermissions(['read:all-companies', 'read:all-contacts'])
  const { reports, quotes, invoices } = getFeatures({ options })
  const hasReports = !!(reports.enabled || crmFlag) && permissionToViewReports
  const hasQuotes = quotes.enabled
  const hasInvoices = invoices.enabled

  const handleDrawerOpen = () => {
    setExpanded(true)
    setOpenNestedCollapsed(null)
  }

  const handleDrawerClose = () => {
    setExpanded(false)
    setOpenNestedCollapsed(null)
  }

  useEffect(() => {
    if (expanded) {
      setCollapseTooltipHidden(true)
    } else {
      const timeout = setTimeout(() => {
        setCollapseTooltipHidden(false)
      }, tooltipDelay)

      return () => {
        clearTimeout(timeout)
      }
    }
  }, [expanded])

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return
    const channel = new BroadcastChannel('subscription-updates')

    const handleMessage = async (event: MessageEvent) => {
      if (event.data === 'crm-addon-added') {
        setCrmTrialExpiration(null)
      } else if (event.data === 'crm-trial-started') {
        const existingTrialExpiration = options?.options?.crm?.trialExpiration
        setCrmTrialExpiration(existingTrialExpiration || moment().add(30, 'day').unix() * 1000)
      }
    }
    channel.addEventListener('message', handleMessage)

    return () => {
      channel.removeEventListener('message', handleMessage)
      channel.close()
    }
  }, [])

  const crmAndQuoteLinks: LinkDefinition[] = [
    {
      title: 'Dashboard',
      to: {
        pathname: '/dashboard/'
      },
      drawer: {
        iconNext: 'fa-solid fa-chart-tree-map'
      },
      show: permissionToViewDashboard
    },
    crmFlag
      ? {
          title: 'Pipelines',
          to: {
            pathname: DEALS_BOARD_PATH,
            params: {
              quoteStatus: 'all'
            }
          },
          children: [
            {
              title: 'Sales',
              drawer: {},
              to: {
                pathname: DEALS_BOARD_PATH,
                search: '?pipeline=sales-pipeline',
                params: {
                  pipeline: 'sales-pipeline'
                },
                state: {
                  pipeline: 'sales-pipeline'
                }
              }
            },
            {
              title: 'Production',
              drawer: {},
              to: {
                pathname: DEALS_BOARD_PATH,
                search: '?pipeline=production-pipeline',
                params: {
                  pipeline: 'production-pipeline'
                },
                state: {
                  pipeline: 'production-pipeline'
                }
              }
            }
          ],
          drawer: {
            iconNext: 'fa-solid fa-circle-dollar'
          },
          show: crmVisible && !isFieldUser,
          onClick: () => track('DealsPageNavigate')
        }
      : {
          title: 'Pipeline',
          to: {
            pathname: DEALS_BOARD_PATH,
            params: {
              quoteStatus: 'all'
            }
          },
          drawer: {
            iconNext: 'fa-solid fa-circle-dollar'
          },
          show: crmVisible && !isFieldUser,
          onClick: () => track('DealsPageNavigate')
        },
    {
      title: objectLabels.quote.plural,
      to: {
        pathname: '/quotes/',
        params: {
          quoteStatus: 'all'
        }
      },
      drawer: {
        iconNext: 'fa-solid fa-clipboard-list'
      },
      show: hasQuotes
    },
    {
      title: 'Invoices',
      to: {
        pathname: '/invoices/',
        params: {
          invoiceStatus: 'all'
        }
      },
      drawer: {
        iconNext: 'fa-solid fa-credit-card-front'
      },
      show: hasInvoices
    },
    crmFlag
      ? {
          title: 'Calendars',
          to: {
            pathname: EVENTS_CALENDAR_PATH
          },
          children: [
            {
              title: 'Sales',
              drawer: {},
              to: {
                pathname: '/events/',
                search: '?tab=calendar'
              }
            },
            {
              title: 'Production',
              drawer: {},
              to: {
                pathname: '/scheduling/',
                search: '?tab=calendar'
              }
            }
          ],
          drawer: {
            iconNext: 'fa-solid fa-calendar'
          },
          show: crmVisible && !isFieldUser
        }
      : {
          title: 'Calendar',
          to: {
            pathname: EVENTS_CALENDAR_PATH
          },
          drawer: {
            iconNext: 'fa-solid fa-calendar'
          },
          show: crmVisible && !isFieldUser,
          onClick: () => track('DealsPageNavigate')
        },
    {
      title: 'Tasks',
      to: {
        pathname: '/tasks/'
      },
      drawer: {
        iconNext: 'fa-solid fa-square-check'
      },
      show: crmVisible && !isFieldUser,
      onClick: () => track('DealsPageNavigate')
    }
  ]

  const peopleLinks: LinkDefinition[] = hidePeople
    ? []
    : [
        {
          title: 'Contacts',
          to: {
            pathname: '/people/contacts/'
          },
          drawer: {
            iconNext: 'fa-solid fa-user-group'
          }
        },
        {
          title: 'Companies',
          to: {
            pathname: '/people/companies/'
          },
          drawer: {
            iconNext: 'fa-solid fa-buildings'
          }
        }
      ]

  let links: LinkDefinition[] = []

  if (isCorporate && !isMasquerading) {
    links = [
      {
        title: objectLabels.locations.plural,
        to: {
          pathname: '/locations/'
        },
        drawer: {
          iconNext: 'fa-solid fa-location-dot'
        }
      },
      {
        title: 'Reports',
        to: {
          pathname: '/reports/'
        },
        drawer: {
          iconNext: 'fa-solid fa-chart-pie-simple'
        },
        show: hasReports
      }
    ].filter((item) => item.show !== false)
  } else {
    const showRealPainterView =
      !!crmFlag && crmVisible && !isFieldUser && (isMasquerading || isSuperadmin) && !!painterAccount

    links = [
      ...crmAndQuoteLinks,
      ...peopleLinks,
      {
        title: 'Reports',
        to: {
          pathname: '/reports/'
        },
        drawer: {
          iconNext: 'fa-solid fa-chart-pie-simple'
        },
        show: hasReports
      },
      {
        title: 'Painter View',
        to: {
          // Support (masquerading/superadmin) opens the real account's data. Accounts LD targets
          // with painterViewDemo (the sales team) get the same entry, but it opens fixed fake data.
          // The support view wins when both apply, so the entry never shows twice.
          pathname: showRealPainterView ? '/scheduling/painter' : '/scheduling/painter-demo'
        },
        drawer: {
          iconNext: 'fa-solid fa-paint-roller'
        },
        // kept below Reports so it doesn't read as a customer feature (ENG-949)
        show: showRealPainterView || !!painterViewDemo
      }
    ].filter((item) => item.show !== false)
  }

  if (isOnCRMTrial && permissionToUpdateBilling) {
    const msRemaining = moment(crmTrialExpiration).diff(moment())
    const daysRemaining = Math.round(msRemaining / (1000 * 60 * 60 * 24))
    const hoursRemaining = Math.max(1, Math.ceil(msRemaining / (1000 * 60 * 60)))

    const title =
      daysRemaining >= 1
        ? `${daysRemaining} day${daysRemaining === 1 ? '' : 's'} left`
        : `${hoursRemaining} hour${hoursRemaining === 1 ? '' : 's'} left`

    links.push({
      title: title,
      to: {
        pathname: '/settings',
        search: '?section=billing-plan'
      },
      drawer: {
        icon: () => <NewAltIcon htmlColor="#FFFFFF" />
      },
      onClick: () => track('CRMTrialPageNavigate')
    })
  }

  if (showDevToolsNav) {
    links.push({
      title: 'Dev Tools',
      drawer: {
        icon: () => <FaceIcon htmlColor="#FFFFFF" />
      },
      onClick: () => setDevState({ ...devState, dismissed: false })
    })
  }

  if (doesNotHaveCRM) {
    links.push({
      title: 'Operations',
      drawer: {
        icon: () => <NewAltIcon htmlColor="#FFFFFF" />
      },
      onClick: () => {
        // Don't open dialog if already on a CRM page (they'll see CRMPromptPage instead)
        if (!isOnCRMPage) {
          track('CRMPromptDialogOpen - SideNav')
          openDialog(OperationsPromptDialog, {
            onConfirm: () => {
              const existingTrialExpiration = options?.options?.crm?.trialExpiration
              setCrmTrialExpiration(existingTrialExpiration || moment().add(30, 'day').unix() * 1000)
            }
          })
        }
      }
    })
  }

  const pathnameMap: [string, string][] = [
    ['/dashboard', '/dashboard/'],
    ['/deals/', DEALS_BOARD_PATH],
    ['/quotes', '/quotes/'],
    ['/invoices', '/invoices/'],
    ['/events', EVENTS_CALENDAR_PATH],
    ['/scheduling/painter', '/scheduling/painter'],
    ['/scheduling', EVENTS_CALENDAR_PATH],
    ['/tasks', '/tasks/'],
    ['/people/contact', '/people/contacts/'],
    ['/people/companies', '/people/companies/'],
    ['/reports', '/reports/']
  ]

  const handleActiveLink = (to: string) => {
    if (!to) return false
    const pathname = location.pathname + location.search
    const cleanPath = pathname.split('?')[0].replace(/\/+$/, '') // remove trailing slashes

    const match = pathnameMap.find(([pathPrefix]) => cleanPath.startsWith(pathPrefix)) || []
    return to === match[1]
  }

  const isOnPresentationNewPage = location.pathname.startsWith('/new-presentation')
  if (isOnPresentationNewPage) {
    return <main>{children}</main>
  }

  const handleActiveChildLink = ({ child, parentActive }: { child: LinkDefinition; parentActive?: boolean }) => {
    if (pageSubtitle && child.title === pageSubtitle && parentActive) {
      return true
    }
    return location.search.includes(child.to.search || '') && location.pathname === child.to.pathname
  }

  return (
    <NavContext.Provider
      value={{
        setOpen,
        sideNavWidth: mdDown ? 0 : sideNavWidth,
        pageSubtitle,
        setPageSubtitle,
        pageChip,
        setPageChip,
        pageActions,
        setPageActions,
        hideBackLink,
        setHideBackLink,
        stickyHeader,
        setStickyHeader,
        pageBreadcrumb,
        setPageBreadcrumb
      }}
    >
      <div className={classes.root}>
        <Drawer
          open={open}
          onClose={() => setOpen(false)}
          variant={mdDown ? 'temporary' : 'permanent'}
          className={classnames(classes.drawer, {
            [classes.drawerOpen]: isExpanded,
            [classes.drawerClose]: !isExpanded,
            'tour-side-nav': true
          })}
          classes={{
            paper: classnames(classes.drawerPaper, {
              [classes.drawerOpen]: isExpanded,
              [classes.drawerClose]: !isExpanded
            })
          }}
        >
          <List className="tour-side-nav-list">
            <NavLink to="/dashboard/" className={classes.link}>
              <img className={classes.logoIcon} src={PaintScout_Icon_White} alt="PaintScout Logo" />
              <img className={classes.logoWordmark} src={PaintScout_Wordmark_White} alt="PaintScout Wordmark" />
            </NavLink>
            {links.map((item) => {
              if (item.children) {
                return (
                  <>
                    <ListItem
                      key={item.title}
                      style={{ position: 'relative', cursor: navBlocked ? 'default' : 'pointer' }}
                      classes={{
                        root: classnames(classes.linkContainer, {
                          [classes.active]: handleActiveLink(item?.to?.pathname),
                          [classes.activeLinkContainer]:
                            handleActiveLink(item?.to?.pathname) && (!isExpanded || !openNested.includes(item.title)),
                          [classes.disabledLink]: navBlocked
                        })
                      }}
                      onClick={(e) => {
                        // Expanding a submenu on a churned account only reveals links that cannot
                        // be followed. The two render guards below (`!navBlocked`) close the same
                        // hole from the other side, for a submenu that was already open when the
                        // account's state changed mid-session.
                        if (navBlocked) return
                        if (isExpanded) {
                          if (openNested.includes(item.title)) {
                            setOpenNested(openNested.filter((title) => title !== item.title))
                          } else {
                            setOpenNested([...openNested, item.title])
                          }
                        } else {
                          if (item.title === openNestedCollapsed) {
                            setOpenNestedCollapsed(null)
                          } else {
                            setFloatingMenuTop(e.currentTarget.getBoundingClientRect().top)
                            setOpenNestedCollapsed(item.title)
                          }
                        }
                      }}
                      disableGutters
                    >
                      {openNested.includes(item.title) ? (
                        <ListItemIcon className={classes.linkIcon}>
                          <i className={item.drawer.iconNext} />
                          {item.drawer.icon && React.createElement(item.drawer.icon)}
                        </ListItemIcon>
                      ) : (
                        <Tooltip
                          title={item.title}
                          key={item.title}
                          placement="right"
                          disabled={isExpanded}
                          icon={<></>}
                          enterDelay={tooltipDelay}
                          enterNextDelay={tooltipDelay}
                        >
                          <ListItemIcon className={classes.linkIcon}>
                            <i className={item.drawer.iconNext} />
                            {item.drawer.icon && React.createElement(item.drawer.icon)}
                          </ListItemIcon>
                        </Tooltip>
                      )}

                      <ListItemText
                        classes={{
                          primary: classnames(classes.linkText, {
                            [classes.activeLinkText]: handleActiveLink(item?.to?.pathname)
                          })
                        }}
                        primary={item.title}
                      />
                      {isExpanded && (
                        <ChevronLeftIcon
                          style={
                            openNested.includes(item.title)
                              ? { transition: TRANSITION_EASE, rotate: '270deg' }
                              : { transform: 'rotate(180deg)', transition: TRANSITION_EASE }
                          }
                        />
                      )}
                    </ListItem>
                    {openNested.includes(item.title) && isExpanded && !navBlocked && (
                      <div className={classes.nestedLinks}>
                        {item.children?.map((child) => {
                          const handleChildClick = () => {
                            track('nav_link_clicked', {
                              linkTitle: child.title,
                              destination: child.to?.pathname
                            })
                            if (child.onClick) {
                              child.onClick()
                            }
                            if (mdDown) {
                              setOpen(false)
                            }
                            // only keep this parent open
                            if (openNested.includes(item.title)) {
                              setOpenNested([item.title])
                            }
                            setOpenNestedCollapsed(null)
                          }
                          const childContent = (
                            <ListItem
                              classes={{
                                root: classnames(classes.linkContainer, {
                                  [classes.activeLinkContainer]: handleActiveChildLink({
                                    child,
                                    parentActive: handleActiveLink(item?.to?.pathname)
                                  })
                                })
                              }}
                              disableGutters
                            >
                              <ListItemText
                                classes={{
                                  primary: classnames(classes.linkText, {
                                    [classes.activeLinkText]: handleActiveChildLink({
                                      child,
                                      parentActive: handleActiveLink(item?.to?.pathname)
                                    })
                                  })
                                }}
                                primary={child.title}
                              />
                              {child.isNew && (
                                <NewAltIcon htmlColor="#FFFFFF" style={{ fontSize: '1rem', marginRight: 4 }} />
                              )}
                            </ListItem>
                          )
                          if (!child.to) {
                            return (
                              <div
                                key={child.title}
                                className={classes.link}
                                onClick={handleChildClick}
                                style={{ cursor: 'pointer' }}
                              >
                                {childContent}
                              </div>
                            )
                          }
                          return (
                            <NavLink
                              to={child.to}
                              key={child.title}
                              className={classes.link}
                              onClick={handleChildClick}
                            >
                              {childContent}
                            </NavLink>
                          )
                        })}
                      </div>
                    )}
                  </>
                )
              }
              const isDevToolsItem = item.title === 'Dev Tools'
              const linkContent = (
                <ListItem
                  classes={{
                    root: classnames(classes.linkContainer, {
                      [classes.activeLinkContainer]: handleActiveLink(item?.to?.pathname),
                      [classes.devToolsActive]: isDevToolsItem && devState.active
                    })
                  }}
                  disableGutters
                >
                  <Tooltip
                    title={item.title}
                    key={item.title}
                    placement="right"
                    disabled={isExpanded}
                    icon={<></>}
                    enterDelay={tooltipDelay}
                    enterNextDelay={tooltipDelay}
                  >
                    <ListItemIcon className={classes.linkIcon}>
                      <i className={item.drawer.iconNext} />
                      {item.drawer.icon && React.createElement(item.drawer.icon)}
                    </ListItemIcon>
                  </Tooltip>
                  <ListItemText
                    classes={{
                      primary: classnames(classes.linkText, {
                        [classes.activeLinkText]: handleActiveLink(item?.to?.pathname)
                      })
                    }}
                    primary={item.title}
                  />
                </ListItem>
              )
              const handleClick = () => {
                track('nav_link_clicked', {
                  linkTitle: item.title,
                  destination: item.to?.pathname
                })
                if (item.onClick) {
                  item.onClick()
                }
                if (mdDown) {
                  setOpen(false)
                }
                if (openNested) {
                  setOpenNested([])
                }
                setOpenNestedCollapsed(null)
              }
              // Rendered as an inert div rather than a NavLink with pointer-events off, so there
              // is no route to follow even by keyboard.
              if (navBlocked) {
                return (
                  <div key={item.title} className={classnames(classes.link, classes.disabledLink)}>
                    {linkContent}
                  </div>
                )
              }
              if (!item.to) {
                return (
                  <div key={item.title} className={classes.link} onClick={handleClick} style={{ cursor: 'pointer' }}>
                    {linkContent}
                  </div>
                )
              }
              return (
                <NavLink to={item.to} key={item.title} className={classes.link} onClick={handleClick}>
                  {linkContent}
                </NavLink>
              )
            })}
          </List>

          {!mdDown && (
            <div style={{ marginTop: 'auto' }}>
              <Tooltip
                classes={{
                  htmlTooltip: classnames(classes.collapseTooltip, {
                    [classes.collapseTooltipOpen]: collapseTooltipHidden
                  })
                }}
                title={'Expand'}
                placement="right"
                disabled={isExpanded}
                icon={<></>}
                enterDelay={tooltipDelay}
                enterNextDelay={tooltipDelay}
              >
                <Button
                  classes={{ root: classes.drawerToggleButton }}
                  variant="dialog"
                  onClick={isExpanded ? handleDrawerClose : handleDrawerOpen}
                >
                  <ChevronLeftIcon
                    style={
                      isExpanded
                        ? { transition: TRANSITION_EASE }
                        : { transform: 'rotate(180deg)', transition: TRANSITION_EASE }
                    }
                  />
                </Button>
              </Tooltip>
            </div>
          )}
        </Drawer>
        {openNestedCollapsed && !isExpanded && !navBlocked && (
          <div
            style={{
              position: 'fixed',
              left: 64,
              top: floatingMenuTop
            }}
            className={classes.floatingMenu}
          >
            {links
              .find((item) => item.title === openNestedCollapsed)
              ?.children?.map((child) => (
                <NavLink
                  to={child.to}
                  key={child.title}
                  className={classes.link}
                  onClick={() => {
                    track('nav_link_clicked', {
                      linkTitle: child.title,
                      destination: child.to?.pathname
                    })
                    setOpenNestedCollapsed(null)
                    if (mdDown) setOpen(false)
                  }}
                >
                  <ListItem
                    classes={{
                      root: classnames(classes.linkContainer, {
                        [classes.activeLinkContainer]: handleActiveChildLink({ child })
                      })
                    }}
                    disableGutters
                  >
                    <ListItemText
                      classes={{
                        primary: classnames(classes.nestedLinkText, {
                          [classes.activeLinkText]: handleActiveChildLink({ child })
                        })
                      }}
                      primary={child.title}
                    />
                  </ListItem>
                </NavLink>
              ))}
          </div>
        )}
        <main className={classes.content} onClick={() => setOpenNestedCollapsed(null)}>
          {children}
        </main>
      </div>
    </NavContext.Provider>
  )
}
